// Campaign audience-segment picker. Multi-select of Brand.demographics
// personas — the set that DIRECTOR_PERSONA_PICKING constrains the
// Director to when it emits routing.target_persona_id per concept.
//
// Empty selection = clear audienceSegments = Director defaults to all
// brand personas (matches pre-audienceSegments behaviour). Save is one
// PUT that replaces the whole set — no per-persona add/remove routes.

import {
  Card, CardBody, VStack, HStack, Text, Heading, Button, Box, Badge,
  Wrap, WrapItem, Spinner, useToast
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { apiJson } from '../../auth/apiFetch';

type Persona = {
  _id: string;
  name: string;
  description?: string;
  toneHint?: string;
  avatarUrl?: string | null;
};

type BrandPersonasResp = {
  _id: string;
  name: string;
  demographics?: Persona[];
};

export function AudienceSegmentsCard({
  campaignId,
  brandId,
  initialAudienceSegments,
  onChanged
}: {
  campaignId: string;
  brandId: string | null;
  initialAudienceSegments: Array<{ personaId: string }>;
  onChanged: () => void;
}) {
  const [personas, setPersonas] = useState<Persona[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set((initialAudienceSegments || []).map(s => String(s.personaId)))
  );
  const [initialSelected, setInitialSelected] = useState<Set<string>>(
    () => new Set((initialAudienceSegments || []).map(s => String(s.personaId)))
  );
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const loadPersonas = useCallback(async () => {
    if (!brandId) { setLoading(false); return; }
    setLoading(true);
    try {
      // GET /api/brand/:id returns { brand: {...} } — unwrap it. The prior
      // version of this file read demographics off the top-level response
      // and silently got undefined every time, so the persona buttons
      // never rendered for any brand.
      const resp = await apiJson<{ brand?: BrandPersonasResp }>(`/api/brand/${brandId}`);
      const brand: Partial<BrandPersonasResp> = resp?.brand ?? {};
      // Backend returns raw demographic docs — mongoose passes the _id
      // through even for {_id:false} historical schemas as long as we're
      // reading a doc that was saved after the flip. Personas without
      // _id are hidden (they can't be referenced from the campaign
      // anyway).
      const withIds = (brand.demographics || []).filter(p => p._id).map(p => ({
        _id:         String(p._id),
        name:        p.name,
        description: p.description || '',
        toneHint:    p.toneHint || '',
        avatarUrl:   p.avatarUrl || null
      }));
      setPersonas(withIds);
    } catch (err) {
      toast({
        title: 'Failed to load brand personas',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 4000
      });
    } finally {
      setLoading(false);
    }
  }, [brandId, toast]);

  useEffect(() => { void loadPersonas(); }, [loadPersonas]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const dirty = selected.size !== initialSelected.size
    || [...selected].some(id => !initialSelected.has(id));

  const save = async () => {
    if (saving || !dirty) return;
    setSaving(true);
    try {
      const res = await apiJson<{ count: number }>(`/api/campaigns/${campaignId}/audience-segments`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personaIds: [...selected] })
      });
      setInitialSelected(new Set(selected));
      toast({
        title: res.count === 0
          ? 'Audience segments cleared'
          : `Saved ${res.count} audience segment(s)`,
        description: res.count === 0
          ? 'Director will default to all brand personas on the next generate.'
          : 'Director will constrain concept picks to this list on the next generate.',
        status: 'success',
        duration: 3500
      });
      onChanged();
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 5000
      });
    } finally {
      setSaving(false);
    }
  };

  const revert = () => setSelected(new Set(initialSelected));

  if (loading) {
    return (
      <Card variant="outline">
        <CardBody>
          <HStack><Spinner size="sm" /><Text fontSize="sm" color="brand.muted">Loading audience personas…</Text></HStack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card variant="outline">
      <CardBody>
        <HStack justify="space-between" mb={3} align="flex-start">
          <VStack align="flex-start" spacing={0}>
            <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em">Audience Segments</Heading>
            <Text fontSize="xs" color="brand.muted" mt={1}>
              Personas the Director will consider for this campaign&apos;s ad concepts. Empty = all brand personas.
            </Text>
          </VStack>
          {dirty && (
            <HStack spacing={2}>
              <Button size="xs" variant="ghost" onClick={revert} isDisabled={saving}>Revert</Button>
              <Button size="xs" colorScheme="purple" onClick={save} isLoading={saving} loadingText="Saving…">
                Save {selected.size === 0 ? '(clear all)' : `(${selected.size})`}
              </Button>
            </HStack>
          )}
        </HStack>

        {(!personas || personas.length === 0) ? (
          <Text fontSize="xs" color="brand.muted" fontStyle="italic">
            No personas defined on this brand. Add personas on the brand page first.
          </Text>
        ) : (
          <VStack align="stretch" spacing={3}>
            <Wrap spacing={2}>
              {personas.map(p => {
                const active = selected.has(p._id);
                return (
                  <WrapItem key={p._id}>
                    <Button
                      size="sm"
                      variant={active ? 'solid' : 'outline'}
                      colorScheme={active ? 'purple' : undefined}
                      onClick={() => toggle(p._id)}
                      title={p.description || p.toneHint || p.name}
                    >
                      {p.name}
                    </Button>
                  </WrapItem>
                );
              })}
            </Wrap>
            <Box p={2} bg="brand.canvas" borderRadius="md" borderWidth="1px" borderColor="brand.border">
              <HStack spacing={2} fontSize="11px" color="brand.muted">
                <Badge fontSize="10px">
                  {selected.size === 0
                    ? 'all brand personas (default)'
                    : `${selected.size} of ${personas.length} selected`}
                </Badge>
                <Text>
                  {selected.size === 0
                    ? 'Director may pick any brand persona per concept.'
                    : 'Director will pick each concept\'s persona from this subset only.'}
                </Text>
              </HStack>
            </Box>
          </VStack>
        )}
      </CardBody>
    </Card>
  );
}
